import { faker } from '@faker-js/faker'
import { DateTime } from 'luxon'
import { match } from 'ts-pattern'

import {
  AnalysisStateResponseSchema,
  PatchStateSchema,
  RepositoryTypeSchema,
  ToolSchema,
  type AnalysisCancellationResponse,
  type AnalysisPaginatedResponse,
  type AnalysisResponse,
  type AnalysisStateResponse,
  type AppScanIntegration,
  type ArnicaIntegration,
  type AzureIntegration,
  type AzureIntegrationRepository,
  type AzurePatchResponse,
  type AzureProject,
  type BitbucketIntegration,
  type BitbucketPatchResponse,
  type BitbucketRepositoryPaginatedResponse,
  type BitbucketWorkspace,
  type BitbucketWorkspacePaginatedResponse,
  type BlackDuckHubIntegration,
  type ChainSummary,
  type CheckmarxEnterpriseIntegration,
  type CheckmarxIntegration,
  type CorrelationSummary,
  type CurrentUserResponse,
  type DatadogIntegration,
  type DimensionAnswer,
  type DimensionAnswerEvidence,
  type DimensionScore,
  type ExploitChainsResponse,
  type FindingAggregateResponse,
  type FindingOverlayCluster,
  type FindingOverlayPriority,
  type FindingOverlayResponse,
  type FindingOverlaysResponse,
  type FindingResponse,
  type FindingResultResponse,
  type GitHubIntegration,
  type GitHubPatchResponse,
  type GitLabIntegration,
  type GitLabPatchResponse,
  type GitLabProjectPaginatedResponse,
  type GitLabProjectResponse,
  type GitPatchResponse,
  type HALLink,
  type Integration,
  type IntegrationPaginatedResponse,
  type IntegrationTypeCountsResponse,
  type IntegrationsBitbucketRepositoryResponse,
  type OpenGrepRule,
  type OpenGrepRuleSet,
  type OpenGrepRulesResponse,
  type OrganizationPreferencesResponse,
  type Patch,
  type PolarisIntegration,
  type RefreshRequestResponse,
  type RefreshRequestsResponse,
  type Repository,
  type RepositoryIntegration,
  type RepositoryIntegrationPaginatedResponse,
  type RepositoryPaginatedResponse,
  type RepositoryRisk,
  type ResultsVerificationResponse,
  type Scale,
  type ScalePaginatedResponse,
  type ScanAggregateResponse,
  type ScanResponse,
  type ScorecardPayload,
  type ScorecardsResponse,
  type Severity,
  type SonarIntegration,
  type ThreatModelResponse,
  type Tool,
  type User,
  type VeracodeIntegration,
  type WorkflowEvent,
  type WorkflowPaginatedResponse,
  type WorkflowResponse,
} from '../src/utils/api-client/api-schemas'
import { SCORECARD_DIMENSIONS, type ScorecardDimensionId } from '../src/utils/threat-model/scorecard-dimensions'

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object | undefined
      ? RecursivePartial<T[P]>
      : T[P]
}

type Resolver<T> = (input?: RecursivePartial<T>) => T

/**
 * Maps a detector to its appropriate scale href.
 * Based on the scale-to-detector mapping used in the application.
 */
export const getScaleHrefForTool = (tool: Tool): string => {
  return match(tool)
    .with('gitlab_dependency_scanning', 'polaris_sast', 'polaris_sca', () => '/api/v1/scales/cvss')
    .with('appscan', () => '/api/v1/scales/appscan')
    .with('veracode', () => '/api/v1/scales/veracode')
    .otherwise(() => '/api/v1/scales/sonar-merged')
}

export function FakeUser(user?: Partial<User>): User {
  return {
    login: faker.internet.username(),
    name: faker.person.fullName(),
    avatar_url: `https://avatars.githubusercontent.com/u/${faker.number.int()}?v=4`,
    email: faker.internet.email(),
    ...user,
  }
}

export function FakeCurrentUser(user?: Partial<CurrentUserResponse>): CurrentUserResponse {
  return {
    login: user?.login ?? faker.internet.username(),
    username: user?.username ?? faker.internet.username(),
    avatar_url: user?.avatar_url ?? `https://avatars.githubusercontent.com/u/${faker.number.int()}?v=4`,
    email: user?.email ?? faker.internet.email(),
    // Matches a default install (see `useIsAdmin`), so an empty array is the exception a test opts
    // into, not the norm.
    roles: user?.roles ?? ['admin'],
    ...user,
  }
}

export function FakeRepository(repository: Partial<Repository> = {}): Repository {
  const repositoryName = repository.name || `${faker.word.sample()}/${faker.word.sample()}-${faker.word.sample()}`
  const repositoryType = repository.type || faker.helpers.arrayElement(RepositoryTypeSchema.options)
  repository.name = repositoryName
  repository.type = repositoryType

  const _links = {
    self: { href: 'https://github.com/test-user/repo-1' },
    'latest-analysis': { href: 'https://github.com/test-user/repo-1/latest-analysis' },
    workflows: { href: 'https://github.com/test-user/repo-1/workflows' },
    'repository-integrations': { href: 'https://github.com/test-user/repo-1/repository-integrations' },
    scans: { href: 'https://github.com/test-user/repo-1/scans' },
  } satisfies Repository['_links']

  return match(repositoryType)
    .with('github', () => {
      if (repository?.type !== 'github') {
        throw new Error('type must be github for github repositories')
      }

      return {
        type: 'github' as const,
        id: repository?.id || faker.string.uuid(),
        name: repositoryName,
        full_name: repository?.full_name || repositoryName,
        default_branch: repository?.default_branch || 'main',
        html_url: repository?.html_url || faker.internet.url(),
        owner: repository?.owner || faker.internet.username(),
        _links,
      }
    })
    .with('gitlab', () => {
      if (repository?.type !== 'gitlab') {
        throw new Error('type must be gitlab for gitlab repositories')
      }

      return {
        type: 'gitlab' as const,
        id: repository?.id || faker.string.uuid(),
        name: repositoryName,
        full_name: repository?.full_name || repositoryName,
        default_branch: repository?.default_branch || 'main',
        namespace: repository?.namespace || 'group/subgroup',
        _links,
      }
    })
    .with('azure', () => {
      if (repository?.type !== 'azure') {
        throw new Error('type must be azure for azure repositories')
      }

      const organization = repository?.organization || 'org-name'
      const project = repository?.project || 'project-name'
      const name = repository?.name || faker.word.sample()

      return {
        type: 'azure' as const,
        id: repository?.id || faker.string.uuid(),
        name,
        full_name: repository?.full_name || `${organization}/${project}/${name}`,
        default_branch: repository?.default_branch || 'main',
        organization,
        project,
        _links,
      }
    })
    .with('bitbucket', () => {
      if (repository?.type !== 'bitbucket') {
        throw new Error('type must be bitbucket for bitbucket repositories')
      }

      return {
        type: 'bitbucket' as const,
        id: repository?.id || faker.string.uuid(),
        name: repositoryName,
        full_name: repository?.full_name || repositoryName,
        default_branch: repository?.default_branch || 'main',
        workspace: repository?.workspace || 'workspace-name',
        slug: repository?.slug || repositoryName.toLowerCase().replace(/\s+/g, '-'),
        _links,
      }
    })
    .with('git', () => {
      if (repository?.type !== 'git') {
        throw new Error('type must be git for git repositories')
      }

      return {
        type: 'git' as const,
        id: repository?.id || faker.string.uuid(),
        name: repositoryName,
        full_name: repository?.full_name || repositoryName,
        default_branch: repository?.default_branch || 'main',
        _links,
      }
    })
    .exhaustive()
}

export function FakePaginatedRepositories(
  paginatedRepositories?: Partial<RepositoryPaginatedResponse>
): RepositoryPaginatedResponse {
  const selfHref = faker.internet.url()
  return {
    _links: {
      first: {
        href: selfHref,
      },
      self: {
        href: selfHref,
      },
    },
    total: 1,
    page: {
      number: 0,
      size: 10,
    },
    _embedded: {
      items: [FakeRepository()],
    },
    ...paginatedRepositories,
  }
}

export function FakeLink(link?: RecursivePartial<HALLink>): HALLink
export function FakeLink(link: undefined, { preserveUndefined }: { preserveUndefined?: true }): undefined
export function FakeLink(
  link: RecursivePartial<HALLink> | undefined,
  options?: { preserveUndefined?: true }
): HALLink | undefined
export function FakeLink(
  link: RecursivePartial<HALLink> | undefined,
  options?: { preserveUndefined?: true }
): HALLink | undefined {
  const preserveUndefined = options?.preserveUndefined === true
  if (preserveUndefined && link === undefined) {
    return undefined
  }

  return {
    href: link?.href ?? faker.internet.url(),
    title: link?.title,
  }
}

export const FakePatch = (patch?: RecursivePartial<Patch>): Patch => {
  patch = patch ?? {}
  const patchType = patch.type ?? faker.helpers.arrayElement(['git', 'github', 'gitlab', 'azure', 'bitbucket'])
  patch.type = patchType

  return match(patch.type)
    .with('git', () => {
      if (patch.type !== 'git') {
        throw new Error('type must be git for git patches')
      }

      return {
        type: 'git',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        branch_name: patch.branch_name ?? faker.git.branch(),
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies GitPatchResponse
    })
    .with('github', () => {
      if (patch.type !== 'github') {
        throw new Error(`type must be github for github patches`)
      }

      return {
        type: 'github',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        html_url: patch.html_url ?? faker.internet.url(),
        pr_number: patch.pr_number ?? faker.number.int({ min: 1, max: 1000 }),
        merge_state: patch.merge_state,
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies GitHubPatchResponse
    })
    .with('gitlab', () => {
      if (patch.type !== 'gitlab') {
        throw new Error(`type must be gitlab for gitlab patches`)
      }

      return {
        type: 'gitlab',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        html_url: patch.html_url ?? faker.internet.url(),
        pr_number: patch.pr_number ?? faker.number.int({ min: 1, max: 1000 }),
        merge_state: patch.merge_state,
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies GitLabPatchResponse
    })
    .with('azure', () => {
      if (patch.type !== 'azure') {
        throw new Error(`type must be azure for azure patches`)
      }

      return {
        type: 'azure',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        html_url: patch.html_url ?? faker.internet.url(),
        pr_number: patch.pr_number ?? faker.number.int({ min: 1, max: 1000 }),
        merge_state: patch.merge_state,
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies AzurePatchResponse
    })
    .with('bitbucket', () => {
      if (patch.type !== 'bitbucket') {
        throw new Error(`type must be bitbucket for bitbucket patches`)
      }

      return {
        type: 'bitbucket',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        html_url: patch.html_url ?? faker.internet.url(),
        pr_number: patch.pr_number ?? faker.number.int({ min: 1, max: 1000 }),
        merge_state: patch.merge_state,
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies BitbucketPatchResponse
    })
    .exhaustive()
}

// Embedded integrations in tests are always tool integrations; polaris is the default shape, but the
// caller's type wins — `scan-findings-table.tsx` renders the integration's name off it, so a fixture
// naming `sonar` must not silently render as Polaris.
//
// The type is matched rather than filled into one literal because the link shapes diverge: Sonar and
// AppScan require `scale` and `webhooks` beside `self`, while every other type declares `self` alone.
// One literal would have to satisfy every branch of the union at once, and no shape does.
const FakeEmbeddedIntegration = (
  integration: NonNullable<NonNullable<RecursivePartial<ScanResponse>['_embedded']>['integration']>
): NonNullable<NonNullable<ScanResponse['_embedded']>['integration']> => {
  const base = {
    id: integration.id ?? 'polaris-default',
    name: integration.name ?? 'Black Duck Polaris',
    source: integration.source ?? 'system',
    // `triage-push` is the only Capability; RecursivePartial erases the array element type, so
    // normalize element-wise. Testing the array instead would make `[]` (truthy) grant the
    // capability it is meant to withhold.
    capabilities: integration.capabilities?.map(() => 'triage-push' as const),
  }
  const self = FakeLink(integration._links?.self)

  return match(integration.type ?? 'polaris')
    .with('sonar', 'appscan', type => ({ ...base, type, _links: { scale: FakeLink(), self, webhooks: FakeLink() } }))
    .otherwise(type => ({ ...base, type, _links: { self } }))
}

export const FakeScan = (scan?: RecursivePartial<ScanResponse>): ScanResponse => {
  const id = scan?.id ?? faker.string.uuid()
  const detector = scan?.detector ?? faker.helpers.arrayElement(ToolSchema.options)
  const scaleHref = scan?._links?.scale?.href ?? getScaleHrefForTool(detector)
  const embeddedIntegration = scan?._embedded?.integration

  return {
    id,
    html_url: faker.internet.url(),
    branch: faker.git.branch(),
    sha: faker.git.commitSha(),
    scanned_at: faker.date.recent().toISOString(),
    imported_at: faker.date.recent().toISOString(),
    ...scan,
    detector,
    _links: {
      self: FakeLink(scan?._links?.self),
      findings: FakeLink(scan?._links?.findings),
      patches: FakeLink(scan?._links?.patches),
      repository: {
        href: scan?._links?.repository?.href ?? `/api/v1/repositories/${faker.string.uuid()}`,
        title: scan?._links?.repository?.title ?? 'Fake Org / Fake Repository',
      },
      scale: { href: scaleHref, title: scan?._links?.scale?.title },
      analyses: { href: scan?._links?.analyses?.href ?? `/api/v1/analyses?scan-id=${id}` },
    },
    _embedded: embeddedIntegration ? { integration: FakeEmbeddedIntegration(embeddedIntegration) } : undefined,
  }
}

export const FakePaginatedScans = (paginatedScans?: Partial<ScanAggregateResponse>): ScanAggregateResponse => {
  const selfHref = faker.internet.url()
  return {
    total: paginatedScans?.total ?? paginatedScans?._embedded?.items?.length ?? 1,
    distinct_branches: paginatedScans?.distinct_branches ?? [],
    page: paginatedScans?.page ?? {
      number: 0,
      size: 10,
      ...paginatedScans?.page,
    },
    _links: {
      first: { href: selfHref },
      self: { href: selfHref },
      ...paginatedScans?._links,
    },
    _embedded: {
      items: paginatedScans?._embedded?.items ?? [FakeScan()],
    },
  }
}

export const FakeThreatModel = (threatModel?: Partial<ThreatModelResponse>): ThreatModelResponse => ({
  status: 'completed',
  phase: 'rendering',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  commitSha: faker.git.commitSha(),
  failureReason: undefined,
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/threat-model' },
    content: { href: '/api/v1/repositories/fake-repository-id/threat-model/content' },
  },
  ...threatModel,
})

export const FakeOpenGrepRules = (openGrepRules?: Partial<OpenGrepRulesResponse>): OpenGrepRulesResponse => ({
  status: 'completed',
  phase: 'generating',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  failureReason: undefined,
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/opengrep-rules' },
    content: { href: '/api/v1/repositories/fake-repository-id/opengrep-rules/content' },
  },
  ...openGrepRules,
})

export const FakeOpenGrepRule = (rule?: Partial<OpenGrepRule>): OpenGrepRule => ({
  rule_id: `pixee.${faker.word.noun()}.${faker.word.noun()}`,
  name: faker.company.catchPhrase(),
  language: 'python',
  noise_level: 'low',
  opengrep_yaml: 'rules:\n  - id: pixee.example\n    message: Example rule\n    severity: WARNING',
  good_code_example: '@permission_classes([IsAuthenticated])\ndef signup(request): ...',
  bad_code_example: '@permission_classes([])\ndef signup(request): ...',
  rationale: faker.lorem.sentence(),
  ...rule,
})

export const FakeOpenGrepRuleSet = (ruleSet?: Partial<OpenGrepRuleSet>): OpenGrepRuleSet => ({
  rules: ruleSet?.rules ?? [FakeOpenGrepRule()],
})

/**
 * A fabricated org-level risk row. No endpoint returns anything like this — see `repository-risk.ts`
 * for why the shape is fabricated; it lives in `api-schemas.ts`'s utility region, with no
 * `AreEquivalent` clause because there is no OpenAPI counterpart to assert against.
 */
export const FakeRepositoryRisk = (risk?: Partial<RepositoryRisk>): RepositoryRisk => ({
  repositoryId: faker.string.uuid(),
  fullName: `${faker.word.sample()}/${faker.word.sample()}-service`,
  defaultBranch: 'main',
  threatModelStatus: 'completed',
  generatedAt: faker.date.recent().toISOString(),
  commitSha: faker.git.commitSha(),
  riskScore: faker.number.int({ min: 0, max: 100 }),
  rationale: 'Internet-facing service handling customer data',
  details:
    'The service terminates public traffic and reaches the orders datastore directly, so a single ' +
    'authorization gap exposes customer records. Two of the threats identified in its model touch the ' +
    'same trust boundary, and neither is covered by an existing test.',
  factors: [
    { dimension: 'attack-surface', detail: 'Reachable from the public internet without authentication' },
    { dimension: 'blast-radius', detail: 'Reads and writes customer contact details' },
  ],
  truePositiveCount: faker.number.int({ min: 0, max: 6 }),
  scanId: faker.string.uuid(),
  ...risk,
})

export const FakeRefreshRequest = (request?: Partial<RefreshRequestResponse>): RefreshRequestResponse => {
  const id = request?.id ?? faker.string.uuid()
  const commitSha = request?.after_sha ?? faker.git.commitSha()
  return {
    id,
    state: 'completed',
    trigger_source: 'direct-push',
    triggered_at: faker.date.recent().toISOString(),
    ref: 'refs/heads/main',
    after_sha: commitSha,
    _links: {
      self: { href: `/api/v1/repositories/fake-repository-id/threat-model/refresh-requests/${id}` },
      repository: { href: '/api/v1/repositories/fake-repository-id' },
      'threat-model': { href: '/api/v1/repositories/fake-repository-id/threat-model' },
      commit: { href: `https://github.com/acme/storefront/commit/${commitSha}` },
    },
    ...request,
  }
}

export const FakeRefreshRequestList = (overrides?: Partial<RefreshRequestsResponse>): RefreshRequestsResponse => ({
  items: overrides?.items ?? [FakeRefreshRequest()],
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/threat-model/refresh-requests' },
    repository: { href: '/api/v1/repositories/fake-repository-id' },
    'threat-model': { href: '/api/v1/repositories/fake-repository-id/threat-model' },
  },
  ...overrides,
})

export const FakeExploitChain = (chain?: Partial<ChainSummary>): ChainSummary => ({
  chainId: faker.string.uuid(),
  title: 'SQL injection to admin account takeover',
  severity: 'critical',
  linkedFindings: [
    {
      findingId: '0',
      rule: 'sql-injection',
      path: 'src/orders/lookup.py',
      kind: 'sast',
      tool: 'semgrep',
      severity: 'high',
      title: 'SQL injection in order lookup',
      fixAvailable: true,
      _links: {
        self: { href: '/api/v1/scans/scan-1/findings/0' },
        finding: { href: '/api/v1/scans/scan-1/findings/0' },
      },
    },
    {
      findingId: '1',
      rule: 'weak-jwt-secret',
      path: 'src/auth/token.py',
      kind: 'sast',
      tool: 'semgrep',
      severity: 'medium',
      title: 'Weak JWT signing secret',
      fixAvailable: false,
      _links: {
        self: { href: '/api/v1/scans/scan-1/findings/1' },
        finding: { href: '/api/v1/scans/scan-1/findings/1' },
      },
    },
  ],
  stepCount: 4,
  detailMarkdown: exampleExploitChainDetailMarkdown,
  ...chain,
})

export const FakeCorrelationSummary = (summary?: Partial<CorrelationSummary>): CorrelationSummary => ({
  strategy: 'all',
  findings: {
    dispatched: 4,
    indexed: 4,
    droppedForBudget: 0,
    byTool: [
      { tool: 'semgrep', kind: 'sast', count: 3, findingIds: ['0', '1', '2'] },
      { tool: 'zap', kind: 'dast', count: 1, findingIds: ['3'] },
    ],
  },
  lenses: [
    { lens: 'flat', proposed: 2, failed: false },
    { lens: 'sink_first', proposed: 1, failed: false },
    { lens: 'capability', proposed: 1, failed: false },
  ],
  candidates: { raw: 4, unique: 3, merged: 2, confirmed: 0, rejected: 2, errored: 0 },
  verdicts: [
    {
      id: 'c1',
      lenses: ['flat', 'sink_first'],
      findingIds: ['0', '3'],
      findings: [
        {
          findingId: '0',
          rule: 'sql-injection',
          path: 'src/orders/lookup.py',
          title: 'SQL injection in order lookup',
          tool: 'semgrep',
        },
        { findingId: '3', rule: 'SQL Injection', path: '/orders', title: 'SQL injection on /orders', tool: 'zap' },
      ],
      hypothesis: 'SQL injection in order lookup → read the users table → forge an admin session',
      verdict: 'rejected',
      reason:
        'The order id is cast to an integer before it reaches the query, and the ZAP payload was rejected with a 400.',
    },
    {
      id: 'c2',
      lenses: ['capability'],
      findingIds: ['1', '2'],
      findings: [
        {
          findingId: '1',
          rule: 'weak-jwt-secret',
          path: 'src/auth/token.py',
          title: 'Weak JWT signing secret',
          tool: 'semgrep',
        },
        {
          findingId: '2',
          rule: 'missing-auth-check',
          path: 'src/admin/routes.py',
          title: 'Admin route without authorization',
          tool: 'semgrep',
        },
      ],
      hypothesis: 'Forge a token with the weak secret → call the unguarded admin route',
      verdict: 'rejected',
      reason:
        'The admin blueprint is mounted behind a login_required middleware, so the missing per-route check is redundant rather than exploitable.',
    },
  ],
  grounding: { chainsIn: 0, chainsOut: 0, referencesRepaired: 0, referencesDropped: 0 },
  phaseDurations: [
    { phase: 'queued', seconds: 1 },
    { phase: 'fetching_code', seconds: 8 },
    { phase: 'loading_inputs', seconds: 2 },
    { phase: 'correlating', seconds: 95 },
    { phase: 'rendering', seconds: 1 },
  ],
  threatModelGeneratedAt: '2026-06-30T09:00:00Z',
  ...summary,
})

export const FakeExploitChains = (exploitChains?: Partial<ExploitChainsResponse>): ExploitChainsResponse => ({
  status: 'completed',
  phase: 'rendering',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  commitSha: faker.git.commitSha(),
  chains: [FakeExploitChain()],
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/exploit-chains' },
    content: { href: '/api/v1/repositories/fake-repository-id/exploit-chains/content' },
  },
  ...exploitChains,
})

const fakeFindingLinks = (findingId: string) => {
  const scanId = faker.string.uuid()
  const href = `/api/v1/scans/${scanId}/findings/${encodeURIComponent(findingId)}`
  return {
    self: { href },
    finding: { href },
  }
}

export const FakeFindingOverlayPriority = (priority?: Partial<FindingOverlayPriority>): FindingOverlayPriority => {
  const findingId = priority?.findingId ?? 'codeql:js/sql-injection@src/routes/login.ts:42'
  return {
    findingId,
    tool: 'codeql',
    rule: 'sql-injection',
    path: 'src/routes/login.ts',
    severity: 'critical',
    score: 92,
    tier: 'critical',
    rationale:
      "Sits on the public 'POST /api/v1/login' entry point, reachable by the 'anonymous' actor. Touches the 'user_credentials' sensitive asset with no mitigating control between the entry point and the sink.",
    _links: fakeFindingLinks(findingId),
    ...priority,
  }
}

export const FakeFindingOverlayCluster = (cluster?: Partial<FindingOverlayCluster>): FindingOverlayCluster => ({
  name: 'Public login surface',
  dimension: 'entry_point',
  subjectName: 'POST /api/v1/login',
  rationale:
    'All three findings in this cluster are reachable from an unauthenticated caller and share the same entry point — remediating the entry-point-level input validation would deprioritize all three at once.',
  findings: [
    {
      findingId: 'codeql:js/sql-injection@src/routes/login.ts:42',
      tool: 'codeql',
      rule: 'sql-injection',
      path: 'src/routes/login.ts',
      severity: 'critical',
      _links: fakeFindingLinks('codeql:js/sql-injection@src/routes/login.ts:42'),
    },
    {
      findingId: 'codeql:js/xss@src/routes/login.ts:58',
      tool: 'codeql',
      rule: 'xss',
      path: 'src/routes/login.ts',
      severity: 'high',
      _links: fakeFindingLinks('codeql:js/xss@src/routes/login.ts:58'),
    },
  ],
  ...cluster,
})

export const FakeFindingOverlayContent = (content?: Partial<FindingOverlayResponse>): FindingOverlayResponse => ({
  narrative:
    'The concentrated risk sits on the public /api/v1/login entry point: two HIGH-severity SQL injection findings are directly reachable from anonymous callers and both touch the primary credentials store. Six additional findings on the internal /admin/* surface are behind the AdminAuthMiddleware trust boundary and are correspondingly deprioritized.',
  priorities: [FakeFindingOverlayPriority()],
  clusters: [FakeFindingOverlayCluster()],
  assumptions: [
    'Assumed the /admin/* routes are exposed only on the internal network; if any /admin/* route is reachable externally, several deprioritized findings should be revisited.',
  ],
  ...content,
})

export const FakeFindingOverlays = (findingOverlays?: Partial<FindingOverlaysResponse>): FindingOverlaysResponse => ({
  status: 'completed',
  phase: 'analyzing',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  summary: {
    priorityCount: 12,
    clusterCount: 3,
    highestTier: 'critical',
  },
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/finding-overlays' },
    content: { href: '/api/v1/repositories/fake-repository-id/finding-overlays/content' },
  },
  ...findingOverlays,
})

export const FakeDimensionAnswerEvidence = (evidence?: Partial<DimensionAnswerEvidence>): DimensionAnswerEvidence => ({
  location: 'src/api/auth.py:42',
  note: 'The login handler enforces bcrypt password verification.',
  ...evidence,
})

export const FakeDimensionAnswer = (answer?: Partial<DimensionAnswer>): DimensionAnswer => ({
  id: faker.string.uuid(),
  question: 'Does the system enforce a baseline authentication policy on all sensitive endpoints?',
  answer: 'yes',
  rationale: 'The authentication middleware guards every route under /api/v1 except health checks.',
  evidence: [FakeDimensionAnswerEvidence()],
  ...answer,
})

const DIMENSION_DESCRIPTIONS: Record<ScorecardDimensionId, string> = {
  'exposure-adjusted-attack-surface': 'How big and how reachable is the surface an attacker could touch.',
  'defense-in-depth-coverage': 'How consistently the system layers redundant controls behind its perimeter.',
  'blast-radius-containment': 'How well a compromise of one component is prevented from spreading.',
  'authentication-posture': 'Strength of identity verification across entry points.',
  'authorization-posture': 'How consistently authenticated callers are constrained to what they are allowed to do.',
  'input-handling-hygiene': 'How safely untrusted data is validated, encoded, and passed to sensitive sinks.',
  'secrets-and-credentials-handling': 'How well the system stores, rotates, and gates access to secrets.',
  'dependency-risk-posture': 'How proactively third-party risk is tracked and mitigated.',
  'transport-and-network-security': 'How much of the data plane is protected in transit.',
  'observability-and-detection': 'How quickly abuse would be noticed and investigable.',
}

/**
 * Dimension ids and display names come from `SCORECARD_DIMENSIONS` so the fixtures cannot name a
 * dimension the app would name differently; only the descriptions are mock prose.
 */
const EXAMPLE_DIMENSIONS: readonly Pick<DimensionScore, 'dimension' | 'displayName' | 'description'>[] =
  SCORECARD_DIMENSIONS.map(({ dimension, displayName }) => ({
    dimension,
    displayName,
    description: DIMENSION_DESCRIPTIONS[dimension],
  }))

export const FakeDimensionScore = (score?: Partial<DimensionScore>): DimensionScore => {
  const preset = faker.helpers.arrayElement(EXAMPLE_DIMENSIONS)
  return {
    dimension: preset.dimension,
    displayName: preset.displayName,
    description: preset.description,
    score: faker.number.float({ min: 0.4, max: 0.95, fractionDigits: 2 }),
    confidence: faker.number.float({ min: 0.5, max: 0.95, fractionDigits: 2 }),
    rationale:
      'Coverage is broad across the primary entry points, but a small number of internal endpoints rely on implicit trust that would fail under a lateral-movement scenario.',
    answers: Array.from({ length: 3 }, () => FakeDimensionAnswer()),
    rubricVersion: 'v1',
    modelUsed: 'claude-opus-4-7',
    generatedAt: faker.date.recent().toISOString(),
    status: 'ok',
    ...score,
  }
}

/**
 * The scorecard payload on its own, without the envelope around it.
 *
 * Exists so callers can spread a scorecard without reaching through
 * `FakeScorecards().scorecard`, which is typed optional and so needed a non-null assertion at every site —
 * against `.claude/rules/avoid-non-null-assertions.md`, and paying for a whole throwaway envelope to read
 * one field off it.
 */
export const FakeScorecard = (scorecard?: Partial<ScorecardPayload>): ScorecardPayload => ({
  systemOverview:
    'A customer-facing storefront backed by a REST API, a relational database, and a third-party payment provider.',
  commitSha: faker.git.commitSha(),
  threatModelDocId: faker.string.uuid(),
  threatModelCommitSha: faker.git.commitSha(),
  dimensions: EXAMPLE_DIMENSIONS.map(preset => FakeDimensionScore(preset)),
  assumptionsOverhang: 0.25,
  generatedAt: faker.date.recent().toISOString(),
  ...scorecard,
})

export const FakeScorecards = (scorecards?: Partial<ScorecardsResponse>): ScorecardsResponse => ({
  status: 'completed',
  phase: 'finalizing',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  commitSha: faker.git.commitSha(),
  failureReason: undefined,
  scorecard: FakeScorecard(),
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/scorecards' },
  },
  ...scorecards,
})

/**
 * A completed response scoring exactly the dimensions given, for a test or story that cares about the
 * scores and nothing else.
 *
 * One definition: this was re-implemented in `repository-risk.columns.stories.tsx` and
 * `repository-risk-drawer.test.tsx` with different parameter annotations, which is how the two were
 * already starting to drift.
 */
export const FakeScorecardsScoring = (...dimensions: Partial<DimensionScore>[]): ScorecardsResponse =>
  FakeScorecards({
    status: 'completed',
    failureReason: undefined,
    scorecard: FakeScorecard({ dimensions: dimensions.map(FakeDimensionScore) }),
  })

export const exampleExploitChainDetailMarkdown = `## SQL injection to admin account takeover

\`\`\`mermaid
graph TD
  A[SQL injection in order lookup] --> B[Dump users table]
  B --> C[Recover password hashes]
  C --> D[Forge admin JWT]
\`\`\`

An attacker exploits an unparameterized order lookup to read the users table, cracks a
weak password hash, then forges an admin JWT using a guessable signing secret.
`

export const FakeScanAnalysis = (
  scanAnalysis?: RecursivePartial<AnalysisResponse>
): AnalysisResponse & { _embedded: { scan: NonNullable<NonNullable<AnalysisResponse['_embedded']>['scan']> } } => {
  const scanId = scanAnalysis?._embedded?.scan?.id ?? faker.string.uuid()
  const analysisId = scanAnalysis?.id ?? faker.string.uuid()

  // Generate realistic timestamps for state transitions
  const now = new Date()
  const baseTime = scanAnalysis?.current_state?.timestamp ? new Date(scanAnalysis.current_state.timestamp) : now

  // Define state transitions with realistic durations
  const generateLogEntries = (finalState: AnalysisStateResponse) => {
    const entries: Required<{ state: AnalysisStateResponse; timestamp: string }>[] = []
    let currentTime = new Date(baseTime)

    // Start with the earliest time (queued)
    // Add random seconds to make it more realistic
    const startMinutes = 20
    const startSeconds = faker.number.int({ min: 0, max: 59 })
    currentTime = new Date(currentTime.getTime() - (startMinutes * 60 + startSeconds) * 1000)
    entries.push({
      state: AnalysisStateResponseSchema.enum.queued,
      timestamp: currentTime.toISOString(),
    })

    // Add in_progress after 2-5 minutes plus random seconds
    const progressMinutes = faker.number.int({ min: 2, max: 5 })
    const progressSeconds = faker.number.int({ min: 0, max: 59 })
    currentTime = new Date(currentTime.getTime() + (progressMinutes * 60 + progressSeconds) * 1000)
    entries.push({
      state: AnalysisStateResponseSchema.enum['in-progress'],
      timestamp: currentTime.toISOString(),
    })

    // Add final state after 10-15 minutes plus random seconds
    if (
      finalState === AnalysisStateResponseSchema.enum.failed ||
      finalState === AnalysisStateResponseSchema.enum.completed
    ) {
      const finalMinutes = faker.number.int({ min: 10, max: 15 })
      const finalSeconds = faker.number.int({ min: 0, max: 59 })
      currentTime = new Date(currentTime.getTime() + (finalMinutes * 60 + finalSeconds) * 1000)
      entries.push({
        state: finalState,
        timestamp: currentTime.toISOString(),
      })
    }

    return entries
  }

  // Determine final state and generate log
  const finalState =
    scanAnalysis?.current_state?.state ??
    faker.helpers.arrayElement([
      AnalysisStateResponseSchema.enum.completed,
      AnalysisStateResponseSchema.enum.failed,
      AnalysisStateResponseSchema.enum['in-progress'],
    ])
  const logEntries = generateLogEntries(finalState)

  return {
    id: analysisId,
    ...scanAnalysis,
    log:
      scanAnalysis?.log && Array.isArray(scanAnalysis.log)
        ? scanAnalysis.log.map(entry => ({
            state: entry.state ?? AnalysisStateResponseSchema.enum.completed,
            timestamp: entry.timestamp ?? now.toISOString(),
          }))
        : logEntries,
    current_state: {
      state: finalState,
      timestamp: logEntries[logEntries.length - 1]?.timestamp ?? now.toISOString(),
      ...scanAnalysis?.current_state,
    },
    _links: {
      self: { href: faker.internet.url(), ...scanAnalysis?._links?.self },
      scan: { href: `/api/v1/scan/${scanId}`, ...scanAnalysis?._links?.scan },
    },
    _embedded: {
      scan: FakeScan({
        ...scanAnalysis?._embedded?.scan,
        id: scanId,
      }),
    },
  }
}

export const FakePaginatedAnalyses = (
  paginatedAnalyses?: RecursivePartial<AnalysisPaginatedResponse>
): AnalysisPaginatedResponse => {
  const resolveLinks: Resolver<AnalysisPaginatedResponse['_links']> = (links = {}) => ({
    first: {
      href: links.first?.href ?? '#',
    },
    self: {
      href: links.self?.href ?? '#',
    },
  })

  const resolvePage: Resolver<AnalysisPaginatedResponse['page']> = (page = {}) => ({
    number: page.number ?? 1,
    size: page.size ?? 1,
  })

  const resolveEmbedded: Resolver<AnalysisPaginatedResponse['_embedded']> = (embedded = {}) => ({
    items: embedded.items?.map(FakeScanAnalysis) ?? [
      FakeScanAnalysis({
        current_state: {
          state: 'completed',
          timestamp: faker.date.recent().toISOString(),
        },
      }),
    ],
  })

  return {
    _links: resolveLinks(paginatedAnalyses?._links),
    total: paginatedAnalyses?.total ?? 1,
    page: resolvePage(paginatedAnalyses?.page),
    _embedded: resolveEmbedded(paginatedAnalyses?._embedded),
  }
}

const FakeAzureProject = (project?: Partial<AzureProject>): AzureProject => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    ...project,
  }
}

export const FakeAzureIntegrationRepository = (
  repository?: Partial<AzureIntegrationRepository>
): AzureIntegrationRepository => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    project: FakeAzureProject(),
    ...repository,
  }
}

export const FakeCreateScanResponse = (createScanResponse?: Partial<ScanResponse>): ScanResponse => {
  return {
    _links: {
      self: FakeLink(),
      findings: FakeLink(),
      patches: FakeLink(),
      repository: FakeLink({ title: faker.company.name() }),
      scale: FakeLink(),
    },
    id: faker.string.uuid(),
    detector: faker.helpers.arrayElement(ToolSchema.options),
    sha: faker.git.commitSha(),
    scanned_at: faker.date.recent().toISOString(),
    imported_at: faker.date.recent().toISOString(),
    html_url: faker.internet.url(),
    branch: faker.git.branch(),
    ...createScanResponse,
  }
}

export function FakeWorkflow(workflow?: Partial<WorkflowResponse>): WorkflowResponse {
  const event = faker.helpers.arrayElement<WorkflowEvent>([
    { type: 'schedule', cadence: 'daily' },
    { type: 'new-scan', branch: 'main' },
    { type: 'pull-request-scan', target_branch: 'main' },
  ])
  const tool = faker.helpers.arrayElement<Tool>(['sonar', 'semgrep', 'codeql', 'snyk'])
  return {
    _links: {
      self: { href: `/api/v1/workflows/${faker.string.uuid()}` },
      repository: { href: `/api/v1/repositories/${faker.string.uuid()}` },
    },
    id: faker.string.uuid(),
    name: faker.string.sample(),
    enabled: true,
    tool,
    created_at: faker.date.recent().toISOString(),
    event,
    action: { type: 'none' },
    ...workflow,
  }
}

export function FakePaginatedWorkflows(
  paginatedWorkflows?: Partial<WorkflowPaginatedResponse>
): WorkflowPaginatedResponse {
  return {
    _links: {
      first: { href: '/api/v1/workflows' },
      self: { href: '/api/v1/workflows' },
    },
    total: 1,
    page: {
      number: 0,
      size: 10,
    },
    _embedded: {
      items: [FakeWorkflow()],
    },
    ...paginatedWorkflows,
  }
}

export const FakeGitlabProject = (gitlabProject?: Partial<GitLabProjectResponse>): GitLabProjectResponse => {
  return {
    path: faker.word.sample(),
    namespace_full_path: faker.company.name(),
    name_with_namespace: faker.company.name(),
    ...gitlabProject,
  }
}

export const FakePaginatedGitlabProjects = (
  paginatedGitlabProjects?: Partial<GitLabProjectPaginatedResponse>
): GitLabProjectPaginatedResponse => {
  const items = paginatedGitlabProjects?._embedded?.items ?? []

  const total = items.length
  const page = paginatedGitlabProjects?.page ?? {
    number: 0,
    size: items.length,
  }
  const selfHref = faker.internet.url()

  return {
    _links: {
      first: { href: selfHref },
      self: { href: selfHref },
    },
    total,
    page,
    _embedded: {
      items,
    },
    ...paginatedGitlabProjects,
  }
}

export const FakeGitHubIntegration = (integration?: Partial<GitHubIntegration>): GitHubIntegration => {
  const id = integration?.id ?? 'github-default'
  return {
    type: 'github',
    name: 'GitHub',
    source: 'system',
    app_name: `pixee-testing-testing`,
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeSonarIntegration = (integration?: Partial<SonarIntegration>): SonarIntegration => {
  // The self link is derived from the id rather than hardcoded, so overriding the id keeps the two
  // consistent — callers that match instances by HAL self link depend on that.
  const id = integration?.id ?? 'sonar-default'
  return {
    type: 'sonar',
    name: 'SonarQube',
    source: 'system',
    base_uri: 'https://sonarcloud.io',
    token_configured: true,
    webhook_secret_configured: true,
    ...integration,
    id,
    _links: integration?._links ?? {
      scale: { href: `/api/v1/scales/${id}` },
      self: { href: `/api/v1/integrations/${id}` },
      webhooks: { href: `/api/v1/integrations/${id}/webhooks` },
    },
  }
}

export const FakeDatadogIntegration = (integration?: Partial<DatadogIntegration>): DatadogIntegration => {
  const id = integration?.id ?? 'datadog-default'
  return {
    type: 'datadog',
    base_uri: 'https://api.datadoghq.com',
    name: 'Production',
    // Datadog is the one type here that is genuinely user-managed and multi-instance; the rest are
    // platform-provisioned singletons, which is what `getIsSystemInstance` keys off.
    source: 'managed',
    health: { status: 'healthy' },
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeCheckmarxIntegration = (integration?: Partial<CheckmarxIntegration>): CheckmarxIntegration => {
  const id = integration?.id ?? 'checkmarx-default'
  return {
    type: 'checkmarx',
    name: 'Checkmarx',
    source: 'system',
    base_uri: 'https://checkmarx.example.com',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeAppScanIntegration = (integration?: Partial<AppScanIntegration>): AppScanIntegration => {
  const id = integration?.id ?? 'appscan-default'
  return {
    type: 'appscan',
    name: 'AppScan',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? {
      scale: { href: `/api/v1/scales/${id}` },
      self: { href: `/api/v1/integrations/${id}` },
      webhooks: { href: `/api/v1/integrations/${id}/webhooks` },
    },
  }
}

export const FakeArnicaIntegration = (integration?: Partial<ArnicaIntegration>): ArnicaIntegration => {
  const id = integration?.id ?? 'arnica-default'
  return {
    type: 'arnica',
    name: 'Arnica',
    source: 'system',
    base_uri: 'https://api.arnica.io',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakePolarisIntegration = (integration?: Partial<PolarisIntegration>): PolarisIntegration => {
  const id = integration?.id ?? 'polaris-default'
  return {
    type: 'polaris',
    name: 'Black Duck Polaris',
    source: 'system',
    base_uri: 'https://polaris.blackduck.com',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeBlackDuckHubIntegration = (
  integration?: Partial<BlackDuckHubIntegration>
): BlackDuckHubIntegration => {
  const id = integration?.id ?? 'black-duck-hub-default'
  return {
    type: 'black-duck-hub',
    name: 'Black Duck Hub',
    // `managed` rather than `system`: a Hub has no system default instance, which is why its
    // repository mapping requires an `integration_id` where every other tool's is optional.
    source: 'managed',
    base_uri: 'https://hub.example.com',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeCheckmarxEnterpriseIntegration = (
  integration?: Partial<CheckmarxEnterpriseIntegration>
): CheckmarxEnterpriseIntegration => {
  const id = integration?.id ?? 'checkmarx-enterprise-default'
  return {
    type: 'checkmarx-enterprise',
    name: 'Checkmarx Enterprise',
    source: 'managed',
    base_uri: 'https://cxsast.example.com',
    credentials_configured: true,
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeVeracodeIntegration = (integration?: Partial<VeracodeIntegration>): VeracodeIntegration => {
  const id = integration?.id ?? 'veracode-default'
  return {
    type: 'veracode',
    name: 'Veracode',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeAzureIntegration = (integration?: Partial<AzureIntegration>): AzureIntegration => {
  const id = integration?.id ?? 'azure-default'
  return {
    type: 'azure',
    name: 'Azure DevOps',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeGitLabIntegration = (integration?: Partial<GitLabIntegration>): GitLabIntegration => {
  const id = integration?.id ?? 'gitlab-default'
  return {
    type: 'gitlab',
    name: 'GitLab',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeBitbucketIntegration = (integration?: Partial<BitbucketIntegration>): BitbucketIntegration => {
  const id = integration?.id ?? 'bitbucket-default'
  return {
    type: 'bitbucket',
    name: 'Bitbucket',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

/**
 * The tool counts derived from a list of integrations, so a stubbed collection and its counts cannot
 * disagree. A test that stubs three Datadog instances gets a count of three without restating it.
 */
export const FakeIntegrationTypeCountsResponse = (items: Integration[] = []): IntegrationTypeCountsResponse => {
  const counts = new Map<Integration['type'], number>()
  for (const item of items) {
    counts.set(item.type, (counts.get(item.type) ?? 0) + 1)
  }
  return {
    _links: { self: { href: '/api/v1/integration-types' } },
    type_counts: [...counts.entries()].map(([type, count]) => ({ count, type })),
  }
}

export const FakeIntegrationPaginatedResponse = (
  paginatedIntegrations?: Partial<IntegrationPaginatedResponse>
): IntegrationPaginatedResponse => {
  const items = paginatedIntegrations?._embedded?.items ?? []

  return {
    _links: {
      first: { href: '/api/v1/integrations' },
      self: { href: '/api/v1/integrations' },
    },
    total: items.length,
    page: {
      number: 0,
      size: 50,
    },
    _embedded: {
      items,
    },
    ...paginatedIntegrations,
  }
}

/**
 * The `sonar-merged` scale as the platform serves it, interleaving Sonar's issue severities with
 * its hotspot severities on one rank line (`SonarScale.mergedHotspotsAndStandardExperienceScale`,
 * ranked by `RankedStandardExperienceSeverity` and `RankedHotspotSeverity`).
 *
 * The order matters more than the values: severity groups render sorted by rank descending, so a
 * fixture that reorders these teaches the wrong thing about the product. `Medium`, `High` and `Low`
 * are hotspot probabilities here, not issue severities, which is why they interleave.
 *
 * Shared with the Storybook story and the unit tests for these filters so the three cannot drift.
 */
export const SONAR_MERGED_SEVERITIES = [
  { label: 'Blocker', rank: 80 },
  { label: 'High', rank: 70 },
  { label: 'Critical', rank: 60 },
  { label: 'Medium', rank: 50 },
  { label: 'Major', rank: 40 },
  { label: 'Minor', rank: 30 },
  { label: 'Low', rank: 20 },
  { label: 'Info', rank: 10 },
]

export const FakeScale = (overrides?: Partial<Pick<Scale, 'id'>>): Scale => {
  const id = overrides?.id ?? 'sonar-merged'
  const _links = {
    self: {
      href: `/api/v1/scales/${id}`,
    },
  }

  return match(id)
    .with(
      'cvss',
      () =>
        ({
          id,
          _links,
          type: 'score',
          min: 0,
          max: 10,
        }) satisfies Scale
    )
    .otherwise(
      () =>
        ({
          id,
          _links,
          type: 'ranked-set',
          severities: SONAR_MERGED_SEVERITIES,
        }) satisfies Scale
    )
}

export const FakePaginatedScales = (overrides?: Partial<ScalePaginatedResponse>): ScalePaginatedResponse => ({
  _links: {
    first: {
      href: '/api/v1/scales',
    },
    self: {
      href: '/api/v1/scales',
    },
  },
  total: 4,
  page: {
    number: 0,
    size: 10,
  },
  _embedded: {
    items: [FakeScale()],
  },
  ...overrides,
})

/**
 * A repository's mapping to a tool.
 *
 * `_links` is overridable like `id` and `html_url`, because `_links.integration` is what names the
 * instance an imported pull authenticates as — the add-findings drawer follows it to fetch that
 * instance, so a test about the bound instance has to be able to set it.
 */
export const FakeRepositoryIntegration = (integration?: Partial<RepositoryIntegration>): RepositoryIntegration => {
  if (!integration) {
    return {
      type: 'sonar' as const,
      id: faker.string.uuid(),
      html_url: faker.internet.url(),
      project_key: faker.string.alphanumeric(8),
      _links: {
        self: { href: faker.internet.url() },
      },
    }
  }
  return (
    match(integration)
      .with({ type: 'sonar' }, ({ type, id, html_url, project_key, _links }) => ({
        type,
        id: id ?? faker.string.uuid(),
        html_url: html_url ?? faker.internet.url(),
        project_key: project_key ?? faker.string.alphanumeric(8),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
      .with({ type: 'veracode' }, ({ type, id, _links }) => ({
        type,
        id: id ?? faker.string.uuid(),
        application_guid: faker.string.uuid(),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
      .with({ type: 'appscan' }, ({ type, id, application_id, html_url, _links }) => ({
        type,
        id: id ?? faker.string.uuid(),
        html_url: html_url ?? faker.internet.url(),
        application_id: application_id ?? faker.string.alphanumeric(8),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
      .with({ type: 'checkmarx' }, ({ type, id, project_id, html_url, _links }) => ({
        type,
        id: id ?? faker.string.uuid(),
        html_url: html_url ?? faker.internet.url(),
        project_id: project_id ?? faker.string.alphanumeric(8),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
      // Polaris mappings are what the add-findings drawer's read-only Instance field is about: the
      // platform creates them from the SARIF, so the drawer states the binding rather than asking.
      .with({ type: 'polaris' }, ({ type, id, polaris_project_id, provenance, created_at, _links }) => ({
        type,
        id: id ?? faker.string.uuid(),
        polaris_project_id: polaris_project_id ?? faker.string.alphanumeric(8),
        provenance: provenance ?? 'SARIF',
        created_at: created_at ?? faker.date.past().toISOString(),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
      // Two identifiers rather than one: a Hub project has many versions and the bill of materials
      // hangs off the version, so a mapping naming only the project resolves to nothing.
      .with({ type: 'black-duck-hub' }, ({ type, id, project_id, version_id, created_at, _links }) => ({
        type,
        id: id ?? faker.string.uuid(),
        project_id: project_id ?? faker.string.uuid(),
        version_id: version_id ?? faker.string.uuid(),
        created_at: created_at ?? faker.date.past().toISOString(),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
      .with({ type: 'checkmarx-enterprise' }, ({ type, id, project_id, created_at, _links }) => ({
        type,
        id: id ?? faker.string.uuid(),
        project_id: project_id ?? faker.number.int({ min: 1, max: 99999 }),
        created_at: created_at ?? faker.date.past().toISOString(),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
      .with({ type: 'arnica' }, ({ type, id, asset_key, provenance, created_at, updated_at, _links }) => ({
        type,
        id: id ?? faker.string.uuid(),
        asset_key: asset_key ?? faker.string.alphanumeric(8),
        provenance: provenance ?? 'pattern-matching',
        created_at: created_at ?? faker.date.past().toISOString(),
        updated_at: updated_at ?? faker.date.past().toISOString(),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
      // TODO: Add support for other types.
      .otherwise(({ _links }) => ({
        type: 'sonar' as const,
        id: faker.string.uuid(),
        html_url: faker.internet.url(),
        project_key: faker.string.alphanumeric(8),
        _links: _links ?? { self: { href: faker.internet.url() } },
      }))
  )
}

export const FakePaginatedRepositoryIntegrations = (
  paginatedIntegrations?: Partial<RepositoryIntegrationPaginatedResponse>
): RepositoryIntegrationPaginatedResponse => {
  const selfHref = faker.internet.url()
  return {
    _links: {
      first: {
        href: selfHref,
      },
      self: {
        href: selfHref,
      },
    },
    total: 1,
    page: {
      number: 0,
      size: 10,
    },
    _embedded: {
      items: [
        FakeRepositoryIntegration({
          type: 'sonar',
          project_key: 'pixee2_webgoat',
        }),
      ],
    },
    ...paginatedIntegrations,
  }
}

export const FakeBitbucketWorkspace = (workspace?: Partial<BitbucketWorkspace>): BitbucketWorkspace => ({
  slug: faker.string.uuid(),
  name: faker.company.name(),
  _links: {
    repositories: { href: '/api/v1/integrations/bitbucket-default/workspaces/pixee/repositories' },
  },
  ...workspace,
})

export const FakePaginatedBitbucketWorkspaces = (
  paginatedBitbucketWorkspaces?: Partial<BitbucketWorkspacePaginatedResponse>
): BitbucketWorkspacePaginatedResponse => ({
  _links: {
    first: { href: '/api/v1/integrations/bitbucket-default/workspaces' },
    self: { href: '/api/v1/integrations/bitbucket-default/workspaces' },
  },
  total: 1,
  page: { number: 0, size: 10 },
  _embedded: { items: [] },
  ...paginatedBitbucketWorkspaces,
})

export const FakeBitbucketIntegrationRepository = (
  repository?: Partial<IntegrationsBitbucketRepositoryResponse>
): IntegrationsBitbucketRepositoryResponse => ({
  name: faker.company.name(),
  slug: faker.string.uuid(),
  workspace: faker.company.name(),
  ...repository,
})

export const FakePaginatedBitbucketRepositories = (
  paginatedBitbucketRepositories?: Partial<BitbucketRepositoryPaginatedResponse>
): BitbucketRepositoryPaginatedResponse => ({
  _links: {
    first: { href: '/api/v1/integrations/bitbucket-default/workspaces/pixee/repositories' },
    self: { href: '/api/v1/integrations/bitbucket-default/workspaces/pixee/repositories' },
  },
  total: 1,
  page: { number: 0, size: 10 },
  _embedded: { items: [] },
  ...paginatedBitbucketRepositories,
})

export const FakeResultsVerificationResponse = (
  verification?: Partial<ResultsVerificationResponse>
): ResultsVerificationResponse => ({
  result: 'not-exploitable',
  headlines: [
    {
      title: 'Path Element(s) Controlled',
      description: 'DIRECTORY',
    },
  ],
  body: '## Findings\n\nThe verification process confirmed this finding.',
  code_blocks: [
    {
      title: 'Proof-of-Concept',
      filename: 'poc.js',
      content: 'console.log("test");',
    },
  ],
  payload_results: {
    columns: ['Payload', 'Output', 'Return Value', 'Had Error'],
    rows: [
      {
        Payload: '../../../etc/passwd',
        Output: 'STDERR: Invalid version argument. Exiting.',
        'Return Value': '1',
        'Had Error': 'True',
      },
      {
        Payload: '1.2.3-..-..-etc-passwd',
        Output:
          'STDOUT: Final file path constructed: /service/versions/v1/releases/1.2.3-..-..-etc-passwd/manifest.json',
        'Return Value': '0',
        'Had Error': 'False',
      },
      {
        Payload: 'C:\\windows\\system32\\config\\sam',
        Output: 'STDERR: Invalid version argument. Exiting.',
        'Return Value': '1',
        'Had Error': 'True',
      },
    ],
  },
  ...verification,
})

export const FakePaginatedFindingsWithResults = (
  paginatedFindingsWithResults?: RecursivePartial<FindingAggregateResponse>
): FindingAggregateResponse => {
  const resolveLinks: Resolver<FindingAggregateResponse['_links']> = (links = {}) => ({
    first: {
      href: links.first?.href ?? '/api/v1/scans/scan-id/findings',
    },
    self: {
      href: links.self?.href ?? '/api/v1/scans/scan-id/findings',
    },
  })

  const resolvePage: Resolver<FindingAggregateResponse['page']> = (page = {}) => ({
    number: page.number ?? 0,
    size: page.size ?? 10,
  })

  const resolveEmbedded: Resolver<FindingAggregateResponse['_embedded']> = (embedded = {}) => ({
    items: embedded.items?.map(item => FakeFindingWithResults(item)) ?? [FakeFindingWithResults()],
  })

  const resolveTriage: Resolver<FindingAggregateResponse['triage']> = (triage = {}) => ({
    status: {
      completed: triage.status?.completed ?? 45,
      failed: triage.status?.failed ?? 5,
      no_recommendations: triage.status?.no_recommendations ?? 10,
      in_progress: triage.status?.in_progress ?? 0,
    },
    outcome: {
      true_positive: triage.outcome?.true_positive ?? 20,
      likely_true_positive: triage.outcome?.likely_true_positive ?? 10,
      inconclusive: triage.outcome?.inconclusive ?? 5,
      false_positive: triage.outcome?.false_positive ?? 8,
      wont_fix: triage.outcome?.wont_fix ?? 2,
      blocked: triage.outcome?.blocked ?? 0,
      excluded: triage.outcome?.excluded ?? 0,
      not_triaged: triage.outcome?.not_triaged ?? 0,
    },
  })

  const resolveFix: Resolver<FindingAggregateResponse['fix']> = (fix = {}) => ({
    status: {
      completed: fix.status?.completed ?? 30,
      failed: fix.status?.failed ?? 3,
      no_recommendations: fix.status?.no_recommendations ?? 5,
      in_progress: fix.status?.in_progress ?? 0,
    },
    outcome: {
      has_fix: fix.outcome?.has_fix ?? 25,
      blocked: fix.outcome?.blocked ?? 3,
      excluded: fix.outcome?.excluded ?? 2,
    },
    confidence: {
      high: fix.confidence?.high ?? 15,
      medium: fix.confidence?.medium ?? 8,
      low: fix.confidence?.low ?? 2,
      no_rating: fix.confidence?.no_rating ?? 0,
    },
  })

  const resolveSca: Resolver<FindingAggregateResponse['sca']> = (sca = {}) => ({
    status: {
      completed: sca.status?.completed ?? 0,
      failed: sca.status?.failed ?? 0,
      blocked: sca.status?.blocked ?? 0,
      excluded: sca.status?.excluded ?? 0,
      in_progress: sca.status?.in_progress ?? 0,
    },
    classification: {
      exploitable: sca.classification?.exploitable ?? 0,
      not_exploitable: sca.classification?.not_exploitable ?? 0,
      inconclusive: sca.classification?.inconclusive ?? 0,
    },
  })

  const resolveComposite: Resolver<FindingAggregateResponse['composite']> = (composite = {}) => ({
    ready_to_fix: composite.ready_to_fix ?? 0,
    no_fix_necessary: composite.no_fix_necessary ?? 0,
  })

  const total = paginatedFindingsWithResults?.total ?? paginatedFindingsWithResults?._embedded?.items?.length ?? 1

  return {
    _links: resolveLinks(paginatedFindingsWithResults?._links),
    total,
    page: resolvePage(paginatedFindingsWithResults?.page),
    _embedded: resolveEmbedded(paginatedFindingsWithResults?._embedded),
    completed_analysis: paginatedFindingsWithResults?.completed_analysis ?? 60,
    in_progress_analysis: paginatedFindingsWithResults?.in_progress_analysis ?? 0,
    triage: resolveTriage(paginatedFindingsWithResults?.triage),
    fix: resolveFix(paginatedFindingsWithResults?.fix),
    sca: resolveSca(paginatedFindingsWithResults?.sca),
    composite: resolveComposite(paginatedFindingsWithResults?.composite),
  }
}

// Findings without a CVSS omit both fields, so these default to undefined unless the caller supplies them.
const buildCvss = (cvss: RecursivePartial<Severity> | undefined): Severity | undefined =>
  cvss ? { label: cvss.label, rank: cvss.rank ?? 0 } : undefined

export const FakeFindingWithResults = (findingWithResults?: RecursivePartial<FindingResponse>): FindingResponse => {
  const findingId = findingWithResults?.id ?? '00000000-0000-0000-0000-000000000000'

  return {
    id: findingId,
    title: findingWithResults?.title ?? 'Sample finding title',
    rule: findingWithResults?.rule ?? 'sample-rule',
    severity: {
      label: findingWithResults?.severity?.label ?? 'medium',
      rank: findingWithResults?.severity?.rank ?? 6.0,
    },
    effective_cvss: buildCvss(findingWithResults?.effective_cvss),
    base_cvss: buildCvss(findingWithResults?.base_cvss),
    html_url: findingWithResults?.html_url ?? 'https://example.com/finding',
    push_state: findingWithResults?.push_state,
    _links: {
      self: {
        href: findingWithResults?._links?.self?.href ?? `/api/v1/scans/scan-id/findings/${findingId}`,
      },
      results: {
        href: findingWithResults?._links?.results?.href ?? `/api/v1/scans/scan-id/findings/${findingId}/results`,
      },
      'representative-results': {
        href:
          findingWithResults?._links?.['representative-results']?.href ??
          `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true`,
      },
    },
    _embedded: {
      'representative-results': FakeResults(findingWithResults?._embedded?.['representative-results']),
      ...(findingWithResults?._embedded?.results && {
        results: FakeResults(findingWithResults._embedded.results),
      }),
    },
  }
}

export const FakeResults = (
  representativeResults?: RecursivePartial<NonNullable<FindingResponse['_embedded']>['representative-results']>
): NonNullable<FindingResponse['_embedded']>['representative-results'] => {
  // Partials flow through `FakeResults._embedded.items` from legacy story fixtures and are passed
  // through to `FakeResult`; widen here so the FakeResult input check stays meaningful for direct callers.
  const items = representativeResults?._embedded?.items?.map(item => FakeResult(item as FakeResultInput)) ?? []

  const selfHref =
    representativeResults?._links?.self?.href ?? `/api/v1/scans/scan-id/findings/1/results?representative=true`
  return {
    _links: {
      first: {
        href: representativeResults?._links?.first?.href ?? selfHref,
      },
      self: {
        href: selfHref,
      },
      finding: {
        href: representativeResults?._links?.finding?.href ?? '#',
      },
    },
    _embedded: {
      items,
    },
    page: {
      number: representativeResults?.page?.number ?? 0,
      size: representativeResults?.page?.size ?? 10,
    },
    total: representativeResults?.total ?? items.length,
  }
}

// Distributive over the FindingResultResponse union so each variant keeps its own per-variant `_links`
// and `_embedded` shapes. `outcome` and (on triage) `latest_push_state`, when supplied, must be full
// values of the matching variant — a recursively-partial one would silently produce a malformed fixture.
type FakeResultInput = FindingResultResponse extends infer R
  ? R extends FindingResultResponse
    ? Omit<RecursivePartial<R>, 'outcome' | 'latest_push_state'> & {
        outcome?: R['outcome']
      } & ('latest_push_state' extends keyof R ? { latest_push_state?: R['latest_push_state'] } : object)
    : never
  : never

export const FakeResult = (result?: FakeResultInput): FindingResultResponse => {
  return match(result)
    .with(
      { type: 'triage' },
      result =>
        ({
          id: result.id ?? 'result-id-placeholder',
          type: result.type,
          completed: result.completed ?? DateTime.now().toISO(),
          // Synthesize a default outcome only when the caller omits the key. An explicit
          // `outcome: undefined` is preserved so callers can model an in-progress (not-yet-completed)
          // result — the backend represents that by omitting `outcome`, never by sending null.
          outcome:
            'outcome' in result
              ? result.outcome
              : ({
                  type: 'completed-triage',
                  suggested_status: 'true_positive',
                  suggested_severity: { label: 'critical', rank: 9.0 },
                  summary: 'This SQL injection vulnerability allows arbitrary database access...',
                } satisfies Extract<FindingResultResponse, { type: 'triage' }>['outcome']),
          latest_push_state: result.latest_push_state,
          _links: {
            self: { href: result._links?.self?.href ?? 'https://example.com/result' },
            finding: { href: result._links?.finding?.href ?? `/api/v1/scans/scan-id/findings/1` },
            analysis: { href: result._links?.analysis?.href ?? 'https://example.com/analysis' },
            article: result._links?.article?.href === undefined ? undefined : { href: result._links.article.href },
            report: result._links?.report?.href === undefined ? undefined : { href: result._links.report.href },
            verification:
              result._links?.verification?.href === undefined ? undefined : { href: result._links.verification.href },
          },
        }) satisfies FindingResultResponse
    )
    .with({ type: 'fix' }, result => {
      return {
        id: result.id ?? 'result-id-placeholder',
        type: result.type,
        completed: result.completed ?? DateTime.now().toISO(),
        outcome: result.outcome,
        _links: {
          self: { href: result._links?.self?.href ?? 'https://example.com/result' },
          finding: { href: result._links?.finding?.href ?? `/api/v1/scans/scan-id/findings/1` },
          analysis: { href: result._links?.analysis?.href ?? 'https://example.com/analysis' },
          changesets: { href: result._links?.changesets?.href ?? 'https://example.com/changesets' },
          patches: { href: result._links?.patches?.href ?? 'https://example.com/patches' },
          'latest-patch': { href: result._links?.patches?.href ?? 'https://example.com/latest-patch' },
        },
        // Preserve _embedded if provided (for latest-patch support)
        ...(result._embedded?.['latest-patch'] && {
          _embedded: {
            'latest-patch': {
              _links: {
                first: {
                  href:
                    result._embedded['latest-patch']._links?.first?.href ??
                    result._embedded['latest-patch']._links?.self?.href ??
                    'https://example.com/latest-patch-collection',
                },
                self: {
                  href:
                    result._embedded['latest-patch']._links?.self?.href ??
                    'https://example.com/latest-patch-collection',
                },
              },
              total: result._embedded['latest-patch'].total ?? 0,
              page: {
                number: result._embedded['latest-patch'].page?.number ?? 0,
                size: result._embedded['latest-patch'].page?.size ?? 10,
              },
              _embedded: {
                items: result._embedded['latest-patch']._embedded?.items?.map(FakePatch) ?? [],
              },
            },
          },
        }),
      } satisfies FindingResultResponse
    })
    .with(
      { type: 'sca' },
      result =>
        ({
          id: result.id ?? 'result-id-placeholder',
          type: result.type,
          completed: result.completed ?? DateTime.now().toISO(),
          outcome: result.outcome,
          _links: {
            self: { href: result._links?.self?.href ?? 'https://example.com/result' },
            finding: { href: result._links?.finding?.href ?? `/api/v1/scans/scan-id/findings/1` },
            analysis: { href: result._links?.analysis?.href ?? 'https://example.com/analysis' },
          },
        }) satisfies FindingResultResponse
    )
    .otherwise(
      () =>
        ({
          id: 'result-id-placeholder',
          type: 'triage',
          completed: DateTime.now().toISO(),
          outcome: {
            type: 'completed-triage',
            suggested_status: 'true_positive',
            suggested_severity: { label: 'critical', rank: 9.0 },
            summary: 'This SQL injection vulnerability allows arbitrary database access...',
          },
          _links: {
            self: { href: 'https://example.com/result' },
            finding: { href: `/api/v1/scans/scan-id/findings/1` },
            analysis: { href: 'https://example.com/analysis' },
          },
        }) satisfies FindingResultResponse
    )
}

export const FakeAnalysisCancellation = (
  cancellation?: Partial<AnalysisCancellationResponse>
): AnalysisCancellationResponse => {
  const id = cancellation?.id ?? faker.string.uuid()
  return {
    id,
    created_at: cancellation?.created_at ?? new Date().toISOString(),
    created_by: cancellation?.created_by ?? faker.internet.email(),
    _links: {
      self: { href: `/api/v1/analysis-cancellations/${id}` },
      scan: { href: '/api/v1/scans/fake-scan-id' },
      analyses: { href: `/api/v1/analysis-cancellations/${id}/analyses` },
      ...cancellation?._links,
    },
  }
}

export function FakeOrganizationPreferences(
  preferences?: Partial<OrganizationPreferencesResponse>
): OrganizationPreferencesResponse {
  const orgId = preferences?.org_id ?? 'default-organization'
  return {
    content: preferences?.content ?? faker.lorem.paragraph(),
    updated_at: preferences?.updated_at ?? '2024-01-01T00:00:00Z',
    updated_by: preferences?.updated_by ?? faker.internet.email(),
    org_id: orgId,
    ...preferences,
    _links: {
      self: { href: `/api/v1/organizations/${orgId}/preferences` },
      ...preferences?._links,
    },
  }
}
